import styled from '@emotion/styled';
import { useMount } from 'ahooks';
import { message, Spin } from 'antd';
import { simpleStringify } from 'components/RouterQuery';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useCrud from 'hooks/useCrud';
import { useMemo, useState, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { fetchToken, list as fetchStoreGroupList } from 'services/api/storeGroup';
import { list as fetchStoreList } from 'services/api/store';
import toLowerCase from 'utils/toLowerCase';
import {
  getNeuronHost,
  useNeuronTranslateMode,
  useNeuronUpload,
  useNeuronStoreIdsSelect,
  useNeuronPlatformList,
  useNeuronKnowledge,
} from 'views/Aide/Qa/Neuron';
import { useNonPlatformQaEnable, useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { permissionNameIdMap } from 'hooks/business/useNewPermisson';
import { getLeYanBotHost } from 'services/api/leyanBotHost';

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  ${({ theme }) =>
    `.${theme.getAntdPrefixedClassName('spin-nested-loading')}, .${theme.getAntdPrefixedClassName(
      'spin-container',
    )}`} {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
`;

export interface NeuronProps {
  storeId: string;
  storeGroupId: string | number;
  path: string;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  editable?: boolean;
  [x: string]: any;
}

function Neuron(props: NeuronProps) {
  const { storeId, storeGroupId, path, platform, region, editable = true, ...otherProps } = props;

  const { t } = useTranslate();

  const lang = useSystemLanguageValue();

  const nonPlatformQaEnable = useNonPlatformQaEnable();

  const { whiteListConfig } = useOrgConfigStateValue();

  const { SHIPPING_AND_LOGISTICS: knowledgeEnable = false } = whiteListConfig || {};

  const [host, setHost] = useState('');

  const [loading, setLoading] = useState(true);

  const {
    item: token,
    loading: tokenLoading,
    fetch: fetchNeuronToken,
  } = useCrud({
    read: fetchToken,
  });

  const { list: storeGroupStoreList, fetchList: fetchStoreGroupStoreList } = useCrud({
    readList: () => {
      const params = {
        platform: nonPlatformQaEnable ? undefined : platform,
        region,
      };

      return Promise.all([
        fetchStoreGroupList(params),
        fetchStoreList({
          ...params,
          permId: permissionNameIdMap.AI智能接待,
        }),
      ]).then(([storeGroupList, storeList]) => {
        const storeGroup = storeGroupList.find((_) => _.id === storeGroupId);

        const { storeIds = [] } = storeGroup || {};

        const storeGroupStoreList = storeIds.reduce<Store.Store[]>((res, id) => {
          const store = storeList.find((_) => _.id === id);

          if (store) {
            res.push(store);
          }

          return res;
        }, []);

        return storeGroupStoreList;
      });
    },
  });

  const platformList = useNeuronPlatformList(storeGroupStoreList || []);

  useMount(() => {
    getNeuronHost().then(setHost);

    fetchNeuronToken(storeGroupId).catch(() => {
      message.error(t('common.feedback.fetchFailed'));
    });

    fetchStoreGroupStoreList().catch(() => {});
  });

  const otherQuery = simpleStringify(otherProps);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [neuronId] = useState(() => Math.random().toString());

  useNeuronUpload({
    neuronId,
    storeList: storeGroupStoreList,
    iframeRef,
  });

  useNeuronTranslateMode(iframeRef, neuronId);

  useNeuronKnowledge(iframeRef, neuronId);

  const { modal } = useNeuronStoreIdsSelect({
    neuronId,
    iframeRef,
    storeGroupStoreList: storeGroupStoreList || [],
  });

  const iframe = useMemo(() => {
    if (!host || !token || !storeGroupStoreList) {
      return null;
    }

    const src = `${host}#/${path}?${simpleStringify({
      auth_token: token,
      lang,
      platform: nonPlatformQaEnable ? undefined : toLowerCase(platform),
      platformList: nonPlatformQaEnable ? platformList : undefined,
      region: toLowerCase(region),
      knowledgeEnable,
      editable,
      leyanBotHost: getLeYanBotHost(),
      neuronId,
    })}${otherQuery ? `&${otherQuery}` : ''}`;

    return (
      <iframe
        key={src}
        ref={iframeRef}
        title="neuron"
        width="100%"
        height="100%"
        frameBorder={0}
        src={src}
        onLoad={() => setLoading(false)}
      />
    );
  }, [
    host,
    path,
    token,
    lang,
    nonPlatformQaEnable,
    platform,
    platformList,
    region,
    otherQuery,
    editable,
    knowledgeEnable,
    storeGroupStoreList,
    neuronId,
  ]);

  return (
    <Container>
      <Spin spinning={loading || !host || tokenLoading}>{iframe}</Spin>
      {modal}
    </Container>
  );
}

export default Neuron;
